// src/components/arrivals/ArrivalsBoard.jsx

import React, { useState, useMemo, useRef, useEffect } from 'react'
import ToggleGroup from '../common/ToggleGroup'
import Legend from '../common/Legend'
import { getArrivalStatus } from '@core/domain/arrivals'
import { arrivalPresentation } from '@core/config/arrivalPresentation'
import TimeCell from './TimeCell'
import {
  STOP_FILTER_MODES,
  getBoardStops,
} from '@core/adapters/LondonArrivalsBoardStops'
import {
  getLondonHour,
  buildHeadwayLookup,
  buildAvgHeadwayLookup,
  buildFutureLookup,
  buildFutureHeadwayLookup,
  resolveHeadwayColors,
  DEFAULT_HEADWAY_COLORS,
} from '@core/adapters/LondonArrivalsBoardData'

export default function ArrivalsBoard({
  stops,
  trips,
  rawArrivals,
  setSelectedStop,
  activeDir,
  onDirChange,
  routeId,
  expectedArrivals = [],
  curtailmentStopSet = null,
}) {
  // ---- state ----
  const [activeMetrics, setActiveMetrics] = useState(() => {
    if (!routeId) return { schedule: true, live: true, headway: true }
    const saved = localStorage.getItem(`arrivalsMetrics:${routeId}`)
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        return { schedule: true, live: true, headway: true, ...parsed }
      } catch {}
    }
    return { schedule: true, live: true, headway: true }
  })

  const [stopFilter, setStopFilter] = useState(() => {
    if (!routeId) return STOP_FILTER_MODES.ALL
    return localStorage.getItem(`arrivalsStopFilter:${routeId}`) || STOP_FILTER_MODES.ALL
  })

  const [showFuture, setShowFuture] = useState(() => {
    if (!routeId) return true
    const saved = localStorage.getItem(`arrivalsShowFuture:${routeId}`)
    return saved === null ? true : saved === 'true'
  })

  const [showSettings, setShowSettings] = useState(false)
  const [earlyThreshold, setEarlyThreshold] = useState(() => {
    if (!routeId) return -2
    const saved = localStorage.getItem(`arrivalsEarlyThreshold:${routeId}`)
    return saved !== null ? parseInt(saved, 10) : -2
  })
  const [lateThreshold, setLateThreshold] = useState(() => {
    if (!routeId) return 5
    const saved = localStorage.getItem(`arrivalsLateThreshold:${routeId}`)
    return saved !== null ? parseInt(saved, 10) : 5
  })
  const [headwayMode, setHeadwayMode] = useState(() => {
    if (!routeId) return 'hour_avg'
    return localStorage.getItem(`arrivalsHeadwayMode:${routeId}`) || 'hour_avg'
  })
  const [headwayToleranceMin, setHeadwayToleranceMin] = useState(() => {
    if (!routeId) return 1
    const saved = localStorage.getItem(`arrivalsHeadwayTolerance:${routeId}`)
    return saved !== null ? parseFloat(saved) : 1
  })

  const dir = activeDir
  const setDir = onDirChange
  const tableWrapRef = useRef(null)

  // ---- persistence ----
  useEffect(() => {
    if (routeId) {
      localStorage.setItem(`arrivalsMetrics:${routeId}`, JSON.stringify(activeMetrics))
    }
  }, [activeMetrics, routeId])

  useEffect(() => {
    if (routeId) {
      localStorage.setItem(`arrivalsStopFilter:${routeId}`, stopFilter)
    }
  }, [stopFilter, routeId])

  useEffect(() => {
    if (routeId) {
      localStorage.setItem(`arrivalsShowFuture:${routeId}`, String(showFuture))
    }
  }, [showFuture, routeId])

  useEffect(() => {
    if (routeId) {
      localStorage.setItem(`arrivalsEarlyThreshold:${routeId}`, String(earlyThreshold))
    }
  }, [earlyThreshold, routeId])

  useEffect(() => {
    if (routeId) {
      localStorage.setItem(`arrivalsLateThreshold:${routeId}`, String(lateThreshold))
    }
  }, [lateThreshold, routeId])

  useEffect(() => {
    if (routeId) {
      localStorage.setItem(`arrivalsHeadwayMode:${routeId}`, headwayMode)
    }
  }, [headwayMode, routeId])

  useEffect(() => {
    if (routeId) {
      localStorage.setItem(`arrivalsHeadwayTolerance:${routeId}`, String(headwayToleranceMin))
    }
  }, [headwayToleranceMin, routeId])

  useEffect(() => {
    if (routeId) {
      const savedMetrics = localStorage.getItem(`arrivalsMetrics:${routeId}`)
      if (savedMetrics) {
        try { setActiveMetrics(JSON.parse(savedMetrics)) } catch {}
      }
      const savedFilter = localStorage.getItem(`arrivalsStopFilter:${routeId}`)
      if (savedFilter) setStopFilter(savedFilter)
      const savedFuture = localStorage.getItem(`arrivalsShowFuture:${routeId}`)
      if (savedFuture !== null) setShowFuture(savedFuture === 'true')
      const savedEarly = localStorage.getItem(`arrivalsEarlyThreshold:${routeId}`)
      if (savedEarly !== null) setEarlyThreshold(parseInt(savedEarly, 10))
      const savedLate = localStorage.getItem(`arrivalsLateThreshold:${routeId}`)
      if (savedLate !== null) setLateThreshold(parseInt(savedLate, 10))
      const savedMode = localStorage.getItem(`arrivalsHeadwayMode:${routeId}`)
      if (savedMode) setHeadwayMode(savedMode)
      const savedTol = localStorage.getItem(`arrivalsHeadwayTolerance:${routeId}`)
      if (savedTol !== null) setHeadwayToleranceMin(parseFloat(savedTol))
    }
  }, [routeId])

  const toggleMetric = (metric) => {
    setActiveMetrics(prev => {
      const newVal = !prev[metric]
      const newState = { ...prev, [metric]: newVal }
      if (!newState.schedule && !newState.live && !newState.headway) {
        return prev
      }
      return newState
    })
  }

  const setAllMetrics = (value) => {
    setActiveMetrics({
      schedule: value,
      live: value,
      headway: value,
    })
  }

  // ---- lookups ----
  const headwayLookup = useMemo(
    () => buildHeadwayLookup(rawArrivals),
    [rawArrivals]
  )

  const avgHeadwayLookup = useMemo(
    () => buildAvgHeadwayLookup(rawArrivals),
    [rawArrivals]
  )

  const futureLookup = useMemo(
    () => buildFutureLookup(expectedArrivals),
    [expectedArrivals]
  )

  const futureHeadwayLookup = useMemo(
    () => buildFutureHeadwayLookup(rawArrivals, expectedArrivals, activeDir),
    [rawArrivals, expectedArrivals, activeDir]
  )

  // ---- board stops ----
  const filteredStops = useMemo(
    () => getBoardStops(stops, stopFilter, curtailmentStopSet, routeId),
    [stops, stopFilter, curtailmentStopSet, routeId]
  )

  // ---- column widths, styles ----
  const wTrip = 56
  const wRun = 56
  const wBonnet = 72
  const fixedColWidth = wTrip + wRun + wBonnet
  const stopColWidth = 115

  const thBase = {
    padding: '9px 2px',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--text-muted)',
    background: 'var(--bg-surface)',
    whiteSpace: 'nowrap',
    borderBottom: '2px solid var(--border)',
    position: 'sticky',
    top: 0,
  }

  const stopColStyle = {
    ...thBase,
    minWidth: stopColWidth,
    width: stopColWidth,
    maxWidth: stopColWidth,
    textAlign: 'center',
    whiteSpace: 'normal',
    padding: '9px 6px',
  }

  const stickyFirstColTh = {
    ...thBase,
    width: fixedColWidth,
    minWidth: fixedColWidth,
    maxWidth: fixedColWidth,
    left: 0,
    zIndex: 30,
    textAlign: 'left',
  }

  const stickyFirstColTd = (rowBg) => ({
    width: fixedColWidth,
    minWidth: fixedColWidth,
    maxWidth: fixedColWidth,
    padding: '6px 2px',
    position: 'sticky',
    left: 0,
    background: rowBg,
    zIndex: 10,
  })

  // ---- resize & scroll effects ----
  useEffect(() => {
    const el = tableWrapRef.current
    if (!el) return
    const adjust = () => {
      const top = el.getBoundingClientRect().top
      const available = Math.max(120, window.innerHeight - top - 24)
      el.style.maxHeight = `${available}px`
    }
    adjust()
    window.addEventListener('resize', adjust)
    return () => window.removeEventListener('resize', adjust)
  }, [trips, stops, stopFilter])

  useEffect(() => {
    const el = tableWrapRef.current
    if (!el || !routeId) return
    const savedScroll = localStorage.getItem(`arrivalsScroll:${routeId}`)
    if (savedScroll) {
      el.scrollTop = parseInt(savedScroll, 10)
    }
    const handleScroll = () => {
      localStorage.setItem(`arrivalsScroll:${routeId}`, String(el.scrollTop))
    }
    el.addEventListener('scroll', handleScroll)
    return () => el.removeEventListener('scroll', handleScroll)
  }, [routeId])

  useEffect(() => {
    if (trips.length > 0 && stops.length > 0) {
      const el = tableWrapRef.current
      if (el && routeId) {
        const saved = localStorage.getItem(`arrivalsScroll:${routeId}`)
        if (saved) el.scrollTop = parseInt(saved, 10)
      }
    }
  }, [trips, stops, routeId])

  const toggleBtnStyle = (isActive) => ({
    padding: '5px 14px',
    borderRadius: 7,
    border: 'none',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    transition: 'all 0.15s',
    background: isActive ? 'var(--bg-surface)' : 'transparent',
    color: isActive ? 'var(--text-primary)' : 'var(--text-muted)',
    boxShadow: isActive ? 'var(--shadow-sm)' : 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    whiteSpace: 'nowrap',
    height: 'auto',
  })

  const isAllActive = activeMetrics.schedule && activeMetrics.live && activeMetrics.headway

  // Stop filter options depend on whether a curtailment set is supplied.
  const stopFilterOptions = [
    { value: STOP_FILTER_MODES.ALL, label: 'All Stops' },
    { value: STOP_FILTER_MODES.TIMING, label: 'Timing Points' },
  ]
  if (curtailmentStopSet) {
    stopFilterOptions.push({ value: STOP_FILTER_MODES.CURTAIL, label: 'Curtailment Points' })
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* ---- Control Bar ---- */}
      <div
        style={{
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          flexWrap: 'wrap',
          borderBottom: '1px solid var(--border-subtle)',
          flexShrink: 0,
        }}
      >
        <ToggleGroup
          options={[
            { value: 'outbound', label: 'Outbound', dot: 'var(--ob-track)' },
            { value: 'inbound', label: 'Inbound', dot: 'var(--ib-track)' },
          ]}
          active={dir}
          onChange={setDir}
        />

        <div style={{ width: 1, height: 22, background: 'var(--border)', flexShrink: 0 }} />

        <ToggleGroup
          options={stopFilterOptions}
          active={stopFilter}
          onChange={setStopFilter}
        />

        <div style={{ width: 1, height: 22, background: 'var(--border)', flexShrink: 0 }} />

        <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>
          Headway vs.
        </span>
        <ToggleGroup
          options={[
            { value: 'hour_avg', label: 'Hour Avg' },
            { value: 'scheduled', label: 'Scheduled' },
          ]}
          active={headwayMode}
          onChange={setHeadwayMode}
        />

        <div style={{ width: 1, height: 22, background: 'var(--border)', flexShrink: 0 }} />

        <div
          style={{
            display: 'flex',
            background: 'var(--bg-elevated)',
            borderRadius: 9,
            padding: 3,
            gap: 2,
            border: '1px solid var(--border)',
          }}
        >
          <button onClick={() => setAllMetrics(true)} style={toggleBtnStyle(isAllActive)}>
            All
          </button>
          <button onClick={() => toggleMetric('schedule')} style={toggleBtnStyle(activeMetrics.schedule)}>
            Schedule
          </button>
          <button onClick={() => toggleMetric('live')} style={toggleBtnStyle(activeMetrics.live)}>
            Live
          </button>
          <button onClick={() => toggleMetric('headway')} style={toggleBtnStyle(activeMetrics.headway)}>
            Headway
          </button>
          <div style={{ width: 1, height: 24, background: 'var(--border)', margin: '0 4px' }} />
          <button onClick={() => setShowFuture(!showFuture)} style={toggleBtnStyle(showFuture)}>
            Show Expected
          </button>
        </div>

        <div style={{ flex: 1 }} />

        <Legend rows={[
          { label: 'Arrival', items: arrivalPresentation.legend.arrival },
          { label: 'Headway', items: arrivalPresentation.legend.headway },
        ]} />

        <button
          onClick={() => setShowSettings(!showSettings)}
          style={{
            ...toggleBtnStyle(showSettings),
            fontSize: 18,
            padding: '0 8px',
            lineHeight: 1,
          }}
        >
          ⚙️
        </button>
      </div>

      {/* ---- Settings Panel ---- */}
      {showSettings && (
        <div
          style={{
            padding: '8px 20px',
            borderBottom: '1px solid var(--border-subtle)',
            background: 'var(--bg-surface)',
            display: 'flex',
          }}
        >
          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-primary)' }}>
                On-time parameters
              </span>
              <label style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
                Early:
                <input
                  type="number"
                  value={earlyThreshold}
                  onChange={(e) => setEarlyThreshold(Number(e.target.value))}
                  style={{
                    width: 45,
                    padding: '2px 4px',
                    fontSize: 10,
                    borderRadius: 4,
                    border: '1px solid var(--border)',
                    background: 'var(--bg-elevated)',
                    color: 'var(--text-primary)',
                  }}
                />
                min
              </label>
              <label style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
                Late:
                <input
                  type="number"
                  value={lateThreshold}
                  onChange={(e) => setLateThreshold(Number(e.target.value))}
                  style={{
                    width: 45,
                    padding: '2px 4px',
                    fontSize: 10,
                    borderRadius: 4,
                    border: '1px solid var(--border)',
                    background: 'var(--bg-elevated)',
                    color: 'var(--text-primary)',
                  }}
                />
                min
              </label>
              <label style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
                Headway tolerance:
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={headwayToleranceMin}
                  onChange={(e) => setHeadwayToleranceMin(Math.max(0, parseFloat(e.target.value) || 0))}
                  style={{
                    width: 45,
                    padding: '2px 4px',
                    fontSize: 10,
                    borderRadius: 4,
                    border: '1px solid var(--border)',
                    background: 'var(--bg-elevated)',
                    color: 'var(--text-primary)',
                  }}
                />
                min
              </label>
            </div>
          </div>
        </div>
      )}

      {/* ---- Table ---- */}
      <div
        ref={tableWrapRef}
        className="table-wrapper"
        style={{ overflow: 'auto', flex: 1, minHeight: 0 }}
      >
        <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <thead style={{ background: 'var(--bg-surface)', position: 'sticky', top: 0, zIndex: 20 }}>
            <tr>
              <th style={stickyFirstColTh}>
                <div style={{ display: 'flex' }}>
                  <div style={{ width: wTrip, textAlign: 'center' }}>Trip</div>
                  <div style={{ width: wRun, textAlign: 'center' }}>Run</div>
                  <div style={{ width: wBonnet, textAlign: 'center' }}>Bonnet</div>
                </div>
              </th>
              {filteredStops.map((stop) => (
                <th key={stop.id} style={stopColStyle}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.3 }}>
                    {stop.name}
                  </div>
                  <div style={{ fontSize: 9, fontFamily: "'JetBrains Mono'", color: 'var(--text-muted)', marginTop: 3 }}>
                    {stop.stop_code_lbsl}
                    {stop.timing_point_code ? ` · ${stop.timing_point_code}` : ''}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trips.map((trip) => {
              const rowBg = trip.direction === 2 ? 'var(--bg-elevated)' : 'var(--bg-surface)'
              const tripKey = String(trip.trip_no_lbsl)
              return (
                <tr key={tripKey} style={{ background: rowBg }}>
                  <td style={stickyFirstColTd(rowBg)}>
                    <div style={{ display: 'flex' }}>
                      <div style={{ width: wTrip, textAlign: 'center' }}>
                        <span style={{ fontSize: 12, fontFamily: "'JetBrains Mono'", fontWeight: 700 }}>
                          {tripKey}
                        </span>
                      </div>
                      <div style={{ width: wRun, textAlign: 'center' }}>
                        <span style={{ fontSize: 12, fontFamily: "'JetBrains Mono'", color: 'var(--text-secondary)' }}>
                          {trip.running_no}
                        </span>
                      </div>
                      <div style={{ width: wBonnet, textAlign: 'center' }}>
                        <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono'", fontWeight: 600, background: 'var(--bg-muted)', padding: '2px 4px', borderRadius: 4 }}>
                          {trip.bonnet_no}
                        </span>
                      </div>
                    </div>
                  </td>

                  {filteredStops.map((stop) => {
                    const stopNumId = Number(stop.id)
                    const [sched, liveIso] = trip.times[stopNumId] ?? [null, null]
                    const futureKey = `${tripKey.trim()}-${String(stop.naptan_id).trim()}`
                    const futureTime = showFuture ? futureLookup.get(futureKey) : null
                    const isFuture = !!futureTime
                    const timeToDisplay = isFuture ? futureTime : liveIso
                    const headwayToDisplay = isFuture
                      ? futureHeadwayLookup.get(futureKey) ?? null
                      : headwayLookup[futureKey] ?? null

                    const scheduledHeadway = trip.scheduleHeadway?.[stopNumId] ?? null

                    const status = getArrivalStatus(sched, timeToDisplay, earlyThreshold, lateThreshold)
                    const statusColor = status.color
                    const statusBg = isFuture ? 'transparent' : status.bgColor

                    let baseline = null
                    if (headwayMode === 'hour_avg') {
                      const hour = timeToDisplay ? getLondonHour(timeToDisplay) : null
                      if (hour !== null) {
                        const avgKey = `${hour}-${String(stop.naptan_id).trim()}`
                        baseline = avgHeadwayLookup[avgKey] ?? null
                      }
                    } else if (headwayMode === 'scheduled') {
                      baseline = scheduledHeadway !== null ? scheduledHeadway / 60 : null
                    }

                    const { color: headwayColor, bg: headwayBg } = resolveHeadwayColors(
                      headwayToDisplay,
                      baseline,
                      headwayToleranceMin,
                      DEFAULT_HEADWAY_COLORS,
                      isFuture
                    )

                    return (
                      <td
                        key={stop.id}
                        style={{
                          textAlign: 'center',
                          padding: '6px 6px',
                          width: stopColWidth,
                          minWidth: stopColWidth,
                          maxWidth: stopColWidth,
                          border: '1px solid var(--border-subtle)',
                          overflow: 'visible',
                          position: 'relative',
                        }}
                      >
                        <TimeCell
                          schedTime={sched}
                          liveIso={timeToDisplay}
                          headway={headwayToDisplay}
                          headwayColor={headwayColor}
                          headwayBg={headwayBg}
                          showSchedule={activeMetrics.schedule}
                          showLive={activeMetrics.live}
                          showHeadway={activeMetrics.headway}
                          isFuture={isFuture}
                          statusColor={statusColor}
                          statusBg={statusBg}
                        />
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}